import React, { useState, useEffect, CSSProperties } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { usePromptBuilder } from '../Utility/ManagePromptContext';

// Define the CSS style with proper TypeScript types
const taskContentLimitedStyle: React.CSSProperties = {
  maxHeight: '3em', // Approximately 2 lines
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box' as any, // Type casting for non-standard properties
  WebkitLineClamp: 2 as any,
  WebkitBoxOrient: 'vertical' as any
};

interface DraggableTaskTableProps {
  tasks: any[];
  modelData?: any[];
  onDragEnd: (result: any) => void;
  onAddTask?: () => void;
  onDeleteTask?: any;
  onEditTask?: (index: number, task: any) => void;
}

const DraggableTaskTable: React.FC<DraggableTaskTableProps> = ({
  tasks,
  modelData,
  onDragEnd,
  onAddTask,
  onDeleteTask,
  onEditTask,
}) => {
  
  const PF = modelData || []; 
  
  const { HandlePromptHistory} = usePromptBuilder();

  const [hoveredInput, setHoveredInput] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const findModelName = (modelId: string, models: any[]) => {
    const model = models?.find(m => m.id === modelId);
    return model?.displayName || 'Unknown Model';
  };

  const toggleRowExpansion = (index: number) => {
    setExpandedRows(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index) 
        : [...prev, index]
    );
  };

  const copyTaskToClipboard = (task: any) => {
    const textToCopy = task.promptMessage;
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        // Optional: Show a toast or notification that copying was successful
        // console.log('Task copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy task:', err);
      });
  };

  // Function to check if text is longer than 2 lines
  const isTextLong = (text: string) => {
    if (!text) return false;
    // Approximate check - if text has more than 100 characters or contains 2+ newlines
    return text.length > 100 || (text.match(/\n/g) || []).length > 1;
  };

  // Helper function to check if inputs exist and are not empty
  const hasInputs = (task: any) => {
    return task?.inputArray != null && 
           typeof (task?.inputArray) !== 'string' && 
           task?.inputArray.length > 0;
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <table className="table theme-table font-regular font-14 mb-0">
            <thead>
                <tr>
                  <th style={{ width: "11%" }} className="w-11">Inputs</th>
                  <th>Output Description</th>
                  <th className="ps-4">Task</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>


        <Droppable droppableId="tasks">
          {(provided:any) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {tasks?.map((task, index) => (
                task.promptMessage ? (
                  <Draggable
                    key={index.toString()}
                    draggableId={index.toString()}
                    index={index}
                  >
                    {(provided:any) => (
                      <tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                      >
                        <td className="ps-4 position-relative w-11" style={{ whiteSpace: "normal", width: "11%" }}>
                          {hasInputs(task) ? (
                            <div>
                              {task?.inputArray?.map((ele:any) => 
                                <span key={ele.inputPlaceholder}>
                                  {PF.includes(ele.inputPlaceholder) ? 
                                    ele.inputPlaceholder + "," : 
                                    ele.inputPlaceholder}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="ps-4 position-relative" style={{ whiteSpace: "normal",maxWidth:"17vw" }}>
                          {task?.outputDescription ? (
                            <span>{task.outputDescription}</span>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="ps-4 position-relative" style={{ whiteSpace: "normal", maxWidth: "25vw" }}>
                          <div style={expandedRows.includes(index) ? undefined : taskContentLimitedStyle}>
                            {task?.promptMessageID ? (
                              <span>
                                {task?.promptMessage
                                  .split(/(\(input-\d+\))/)
                                  .map((part:any, partIndex:any) => {
                                    const match = part.match(/\((input-\d+)\)/);
                                    if (match) {
                                      const inputValueOrder = match[1];
                                      const label = PF.find(field => field.inputValueOrder === inputValueOrder)?.labelName;

                                      return (
                                        <span
                                          key={partIndex}
                                          onMouseEnter={() => setHoveredInput(inputValueOrder)}
                                          onMouseLeave={() => setHoveredInput(null)}
                                          className={`cursor-pointer ${hoveredInput === inputValueOrder ? "bold-text" : ""}`}
                                        >
                                          {hoveredInput === inputValueOrder ? (label ? label : "Code snippet") : part}
                                        </span>
                                      );
                                    }
                                    return part;
                                  })}
                              </span>
                            ) : (
                              task?.promptMessage
                            )}
                          </div>
                          {isTextLong(task?.promptMessage) && (
                            <button 
                              className="btn btn-link p-0 mt-1 text-primary" 
                              onClick={() => toggleRowExpansion(index)}
                            >
                              {expandedRows.includes(index) ? "See less" : "See more"}
                            </button>
                          )}
                        </td>
                        <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                          {onEditTask && (
                            <img 
                              src="images-new/edit-icons.svg" 
                              onClick={() => {
                                onEditTask(index, task);
                              }}
                              alt="edit-icon"
                              className="me-3 cp-icon-sty"
                              style={{ cursor: 'pointer' }}
                            />
                          )}

                          <img 
                            src="images/Default/copy-icon.svg" 
                            onClick={() => copyTaskToClipboard(task)}
                            alt="copy-icon"
                            className="me-3 cp-icon-sty"
                            style={{ cursor: 'pointer' }}
                            title="Copy task"
                          />

                          {onDeleteTask && (
                            <img
                              src="images/Default/delete-icon.svg"
                              alt="delete-icon"
                              className="me-3 cp-icon-sty"
                              onClick={() => onDeleteTask(index)}
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                        </td>
                      </tr>
                    )}
                  </Draggable>
                ) : null
              ))}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      </table>
    </DragDropContext>
  );
};

export default DraggableTaskTable;